/**
 * 奖池分档编辑器：一张「面额 × 份数」的表，加只读的分布柱图与汇总指标。
 *
 * 背景：奖池码在创建时把总额拆成一份份预先洗牌，兑换时按序取走（后端 activation.py 的 prize_pool）。运营要的是
 *   「总额确定、每档几份」，所以编辑的对象是分档表，份数合计必须精确等于奖池容量（码数量 × 总次数上限）。
 * 设计意图：Alice 用拖拽直方图调分布；这里改为表格输入加只读柱图，键盘可用、数字精确，分布形状一眼可见。
 *   金额以花瓣填写（整花瓣），提交时由调用方换成 unit。
 * 关键约束：最多 64 档（后端上限）；份数与面额只收整数；不一致时只提示，不阻止编辑，由「生成」按钮判断是否可提交。
 */
import { UNITS_PER_PETAL } from '@ap/api-client/money'
import { IntInput, useU } from '../kit-users.tsx'

export interface TierDraft {
  amount: string
  count: string
}

/** 把草稿换成后端的分档（unit）；有无效行返回 null。 */
export function tiersToApi(tiers: TierDraft[]): { amount_units: number; count: number }[] | null {
  const out: { amount_units: number; count: number }[] = []
  for (const t of tiers) {
    const a = Number(t.amount)
    const c = Number(t.count)
    if (!Number.isInteger(a) || a <= 0 || !Number.isInteger(c) || c <= 0) return null
    out.push({ amount_units: a * UNITS_PER_PETAL, count: c })
  }
  return out.length ? out : null
}

export function tierStats(tiers: TierDraft[]) {
  let shares = 0
  let total = 0
  let min = Infinity
  let max = 0
  for (const t of tiers) {
    const a = Number(t.amount) || 0
    const c = Number(t.count) || 0
    shares += c
    total += a * c
    if (c > 0 && a > 0) {
      min = Math.min(min, a)
      max = Math.max(max, a)
    }
  }
  return { shares, total, min: Number.isFinite(min) ? min : 0, max, avg: shares ? total / shares : 0 }
}

export function PoolEditor({ tiers, onChange, size }: { tiers: TierDraft[]; onChange: (t: TierDraft[]) => void; size: number }) {
  const { t, G, tag } = useU()
  const P = G.codes.gen
  const st = tierStats(tiers)
  const peak = Math.max(1, ...tiers.map((x) => Number(x.count) || 0))
  const patch = (i: number, p: Partial<TierDraft>) => onChange(tiers.map((x, k) => (k === i ? { ...x, ...p } : x)))
  const fmt = (n: number) => Math.round(n * 100) / 100
  const sorted = tiers
    .map((x, i) => ({ ...x, i }))
    .sort((a, b) => Number(a.amount) - Number(b.amount))

  return (
    <div>
      <div className="u2-note" style={{ marginTop: 0 }}>
        {t(P.poolSize)}: <strong>{size.toLocaleString(tag)}</strong> ({t(P.poolSizeHint)}). {t(P.poolNoShape)}
      </div>
      <div className="user-detail-table-wrap" style={{ marginTop: 8 }}>
        <table style={{ minWidth: 0 }}>
          <thead>
            <tr>
              <th>{t(P.poolTierAmount)}</th>
              <th>{t(P.poolTierCount)}</th>
              <th>{t(P.poolTierShare)}</th>
              <th>{t(P.poolTierTotal)}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tiers.map((x, i) => {
              const a = Number(x.amount) || 0
              const c = Number(x.count) || 0
              return (
                <tr key={i}>
                  <td>
                    <IntInput value={x.amount} onChange={(v) => patch(i, { amount: v })} style={{ width: 110, padding: '6px 8px', border: '1px solid var(--border-medium)', borderRadius: 6, background: 'var(--bg-input)' }} />
                  </td>
                  <td>
                    <IntInput value={x.count} onChange={(v) => patch(i, { count: v })} style={{ width: 110, padding: '6px 8px', border: '1px solid var(--border-medium)', borderRadius: 6, background: 'var(--bg-input)' }} />
                  </td>
                  <td>{st.shares ? `${fmt((c * 100) / st.shares)}%` : '—'}</td>
                  <td>{(a * c).toLocaleString(tag)}</td>
                  <td>
                    <button className="btn btn-secondary btn-sm" disabled={tiers.length <= 1} onClick={() => onChange(tiers.filter((_, k) => k !== i))}>
                      {t(P.poolRemove)}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" disabled={tiers.length >= 64} onClick={() => onChange([...tiers, { amount: '', count: '' }])}>
          {t(P.poolAdd)}
        </button>
        <span style={{ fontSize: 12, color: st.shares === size ? 'var(--text-secondary)' : 'var(--error-text)' }}>
          {st.shares === size
            ? t(P.poolMetrics, { avg: fmt(st.avg).toLocaleString(tag), min: st.min.toLocaleString(tag), max: st.max.toLocaleString(tag), total: st.total.toLocaleString(tag) })
            : t(P.poolMismatch, { have: st.shares.toLocaleString(tag), need: size.toLocaleString(tag) })}
        </span>
      </div>
      {sorted.some((x) => Number(x.count) > 0) && (
        <div className="u2-bar" aria-hidden>
          {sorted.map((x) => (
            <div key={x.i} title={`${x.amount || 0} × ${x.count || 0}`} style={{ height: `${Math.max(3, ((Number(x.count) || 0) / peak) * 100)}%` }} />
          ))}
        </div>
      )}
    </div>
  )
}
